import { Modal, ModalContent, ModalHeader, ModalBody, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button, Spinner, Tooltip, Input } from "@heroui/react";
import { Info, Paperclip } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

interface EmailMessage {
    id: string;
    subject: string;
    from: {
        emailAddress: {
            address: string;
        }
    };
    receivedDateTime: string;
    body: {
        content: string;
    };
    hasAttachments: boolean;
    attachments?: Array<{
        id: string;
        name: string;
        size: number;
        contentType: string;
    }>;
    editedSubject?: string;
}

interface EmailImportModalProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onEmailsImported: () => void;
}

export const EmailImportModal = ({ isOpen, onOpenChange, onEmailsImported }: EmailImportModalProps) => {
    const { session } = useSession();
    const [emails, setEmails] = useState<EmailMessage[]>([]);
    const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set([]));
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingEmails, setIsFetchingEmails] = useState(false);
    const [emailSubjectEdits, setEmailSubjectEdits] = useState<Map<string, string>>(new Map());
    const [showEditSubjectModal, setShowEditSubjectModal] = useState(false);
    const [currentEditEmail, setCurrentEditEmail] = useState<EmailMessage | null>(null);
    const [editedSubject, setEditedSubject] = useState("");
    const [editSubjectError, setEditSubjectError] = useState("");

    const fetchEmails = async () => {
        try {
            setIsFetchingEmails(true);
            const response = await fetchWithAuth(
                `${API_URL}/email/list-unread-acquisitions`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setEmails(data);
        } catch (error) {
            console.error('Error fetching emails:', error);
            toast.error('Failed to fetch emails');
        } finally {
            setIsFetchingEmails(false);
        }
    };

    const validateSubject = (subject: string) => {
        if (subject.length > 40) {
            return false;
        }
        return true;
    };

    const handleImportSelected = async () => {
        try {
            const messagesToEdit = emails
                .filter(email => selectedEmails.has(email.id))
                .filter(email => {
                    const subjectToCheck = emailSubjectEdits.get(email.id) || email.subject;
                    return !validateSubject(subjectToCheck);
                });

            if (messagesToEdit.length > 0) {
                setCurrentEditEmail(messagesToEdit[0]);
                setEditedSubject(messagesToEdit[0].subject);
                setShowEditSubjectModal(true);
                return;
            }
            
            await processImport();
        } catch (error) {
            console.error('Error importing emails:', error);
            toast.error('Failed to import emails');
            setIsLoading(false);
        }
    };

    const processImport = async (editedSubjectsMap = emailSubjectEdits) => {
        try {
            setIsLoading(true);
            console.log("Starting import process...");
            const messageIds = Array.from(selectedEmails);
            
            // Create a customSubjects object from the provided map
            const customSubjects: { [key: string]: string } = {};
            messageIds.forEach(id => {
                const editedSubject = editedSubjectsMap.get(id);
                if (editedSubject) {
                    (customSubjects as any)[id] = editedSubject;
                    console.log(`Setting custom subject for email ${id}: "${editedSubject}"`);
                }
            });
            
            console.log("All edited subjects:", Array.from(editedSubjectsMap.entries()));
            
            // Send both messageIds and customSubjects to backend
            const emailsToImport = {
                messageIds: messageIds,
                customSubjects: Object.keys(customSubjects).length > 0 ? customSubjects : undefined
            };

            console.log("Sending import request with data:", JSON.stringify(emailsToImport));
            
            const response = await fetchWithAuth(
                `${API_URL}/email/import-selected`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(emailsToImport)
                },
                session
            );
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Import failed: ${errorText}`);
            }
            
            toast.success('Selected emails imported successfully');
            onEmailsImported();
            onOpenChange(false);
        } catch (error) {
            console.error('Error during email import:', error);
            toast.error(`Failed to import emails: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setSelectedEmails(new Set([]));
            setIsLoading(false);
        }
    };

    const handleEditSubject = () => {
        if (!currentEditEmail) return;
        
        if (editedSubject.length > 40) {
            setEditSubjectError("Naam mag niet langer zijn dan 40 tekens");
            return;
        }

        // Update the Map state with the edited subject
        const newMap = new Map(emailSubjectEdits);
        newMap.set(currentEditEmail.id, editedSubject);
        setEmailSubjectEdits(newMap);
        
        // Also update the email in the emails array for UI consistency
        setEmails(emails.map(email => 
            email.id === currentEditEmail.id 
                ? {...email, subject: editedSubject} 
                : email
        ));

        // Log the edit immediately to confirm it happened
        console.log(`[DEBUG] Set custom subject for ${currentEditEmail.id}: "${editedSubject}"`);
        console.log(`[DEBUG] Current map contents:`, Array.from(newMap.entries()));
        
        const remainingEmailsToEdit = emails
            .filter(email => selectedEmails.has(email.id))
            .filter(email => {
                if (email.id === currentEditEmail.id) return false;
                const subjectToCheck = newMap.get(email.id) || email.subject;
                return !validateSubject(subjectToCheck);
            });

        console.log(`${remainingEmailsToEdit.length} emails still need editing after this one`);

        if (remainingEmailsToEdit.length > 0) {
            setCurrentEditEmail(remainingEmailsToEdit[0]);
            setEditedSubject(remainingEmailsToEdit[0].subject);
            setEditSubjectError("");
        } else {
            setShowEditSubjectModal(false);
            setCurrentEditEmail(null);
            
            // Instead of using setTimeout, pass the updated map directly to processImport
            processImport(newMap);
        }
    };

    const handleCancelEdit = () => {
        setShowEditSubjectModal(false);
        setCurrentEditEmail(null);
        setEditSubjectError("");
        setSelectedEmails(new Set([]));
    };

    const handleMarkAsRead = async () => {
        try {
            setIsLoading(true);
            const messageIds = Array.from(selectedEmails);
            console.log("Selected message IDs:", messageIds); // Debug log

            if (messageIds.length === 0) {
                toast.error('No emails selected');
                return;
            }

            await fetchWithAuth(
                `${API_URL}/email/mark-as-read`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ messageIds })
                },
                session
            );
            toast.success('Emails marked as read');
            fetchEmails(); // Refresh the list
            setSelectedEmails(new Set()); // Clear selection
        } catch (error) {
            console.error('Error marking emails as read:', error);
            toast.error('Failed to mark emails as read');
        } finally {
            setSelectedEmails(new Set([]));
            setIsLoading(false);
        }
    };

    const fetchEmailDetails = async (emailId: string) => {
        try {
            setIsLoading(true);
            const response = await fetchWithAuth(
                `${API_URL}/email/message-details/${emailId}`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            
            if (!data) {
                throw new Error('Failed to fetch email details');
            }

            // Update the email in the list with attachment details
            setEmails(prevEmails => 
                prevEmails.map(email => 
                    email.id === emailId 
                        ? { ...email, attachments: data.attachments }
                        : email
                )
            );
        } catch (error) {
            console.error('Error fetching email details:', error);
            toast.error('Failed to load attachments');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch emails when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchEmails();
        }
    }, [isOpen]);

    const downloadAttachment = async (messageId: string, attachment: any) => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/email/attachment/${messageId}/${attachment.id}`,
                { method: "GET" },
                session,
            );

            // Create a blob from the response
            const blob = await response.blob();
            
            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.name;
            document.body.appendChild(a);
            a.click();
            
            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading attachment:', error);
            toast.error('Failed to download attachment');
        }
    };

    const renderEmailPreview = (email: EmailMessage) => {
        return (
            <div className="flex items-center gap-2">
                <span>{emailSubjectEdits.get(email.id) || email.subject}</span>
                <Tooltip
                    content={
                        <div className="max-w-[800px] max-h-[80vh] overflow-y-auto">
                            <p className="font-bold mb-2">Van: {email.from.emailAddress.address}</p>
                            <p className="font-bold mb-2">Ontvangen: {new Date(email.receivedDateTime).toLocaleString()}</p>
                            {email.hasAttachments && (
                                <div className="mb-2">
                                    <p className="font-bold">Bijlagen:</p>
                                    <div className="mt-1 space-y-1">
                                        {email.attachments ? (
                                            email.attachments.map(attachment => (
                                                <div 
                                                    key={attachment.id} 
                                                    className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-sm cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        downloadAttachment(email.id, attachment);
                                                    }}
                                                >
                                                    <Paperclip className="w-4 h-4" />
                                                    <span>{attachment.name}</span>
                                                    <span className="text-sm text-gray-500">
                                                        ({Math.round(attachment.size / 1024)}KB)
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <Button 
                                                size="sm" 
                                                variant="light"
                                                isLoading={isLoading}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    fetchEmailDetails(email.id);
                                                }}
                                            >
                                                {isLoading ? 'Laden...' : 'Laad bijlagen'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <p className="font-bold">Inhoud:</p>
                            <div
                                className="mt-2"
                                dangerouslySetInnerHTML={{ __html: email.body?.content || '' }}
                            />
                        </div>
                    }
                    placement="left"
                    showArrow
                    classNames={{
                        base: "before:bg-default-200",
                        content: [
                            "py-2 px-4 shadow-xl",
                            "rounded-lg"
                        ],
                    }}
                >
                    <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 cursor-pointer text-neutral-secondary-mid hover:text-neutral-primary" />
                        {email.hasAttachments && (
                            <Paperclip className="w-4 h-4 text-neutral-secondary-mid" />
                        )}
                    </div>
                </Tooltip>
            </div>
        );
    };

    const handleSelectionChange = (keys: any) => {
        // Create an array from the new selection keys
        const newSelection = Array.from(keys) as string[];
        
        // Check if any of the selected emails have subjects that are too long
        const tooLongSubjects = newSelection
            .map(id => emails.find(email => email.id === id))
            .filter(email => email && email.subject.length > 40);
        
        if (tooLongSubjects.length > 0) {
            // Show the edit dialog for the first email with a too-long subject
            setCurrentEditEmail(tooLongSubjects[0] as EmailMessage);
            setEditedSubject(tooLongSubjects[0]?.subject || '');
            setEditSubjectError("");
            setShowEditSubjectModal(true);
        }
        
        // Update the selection
        setSelectedEmails(new Set(newSelection));
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onOpenChange={(open) => {
                    if (!isLoading && !showEditSubjectModal) {
                        onOpenChange(open);
                    }
                }}
                size="5xl"
                scrollBehavior="inside"
            >
                <ModalContent className="max-h-[90vh]">
                    <ModalHeader>Import Emails from acquisitions@yally.be</ModalHeader>
                    <ModalBody className="overflow-y-auto">
                        <div className="flex gap-2 mb-4">
                            <Button
                                color="primary"
                                onClick={handleImportSelected}
                                isDisabled={selectedEmails.size === 0 || isLoading}
                                isLoading={isLoading}
                            >
                                Importeer acquisitiedossier(s) ({selectedEmails.size})
                            </Button>
                            <Button
                                onClick={handleMarkAsRead}
                                isDisabled={selectedEmails.size === 0 || isLoading}
                                isLoading={isLoading}
                            >
                                Archiveer dossier(s) ({selectedEmails.size})
                            </Button>
                        </div>

                        {isFetchingEmails ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <Spinner size="lg" color="primary" />
                                <p className="mt-4 text-neutral-secondary-mid">Loading emails...</p>
                            </div>
                        ) : (
                            <Table
                                selectionMode="multiple"
                                selectedKeys={selectedEmails}
                                onSelectionChange={handleSelectionChange}
                                aria-label="Unread emails table"
                            >
                                <TableHeader>
                                    <TableColumn>Subject</TableColumn>
                                    <TableColumn>From</TableColumn>
                                    <TableColumn>Received</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {emails.map((email) => (
                                        <TableRow key={email.id}>
                                            <TableCell>{renderEmailPreview(email)}</TableCell>
                                            <TableCell>{email.from.emailAddress.address}</TableCell>
                                            <TableCell>{new Date(email.receivedDateTime).toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={showEditSubjectModal}
                hideCloseButton={true}
                size="md"
            >
                <ModalContent>
                    <ModalHeader>Acquisitie naam inkorten</ModalHeader>
                    <ModalBody>
                        <p className="mb-4">
                            De acquisitie naam is te lang (max 40 tekens). Pas de naam aan:
                        </p>
                        <Input
                            value={editedSubject}
                            onChange={(e) => {
                                setEditedSubject(e.target.value);
                                if (e.target.value.length > 40) {
                                    setEditSubjectError("Naam mag niet langer zijn dan 40 tekens");
                                } else {
                                    setEditSubjectError("");
                                }
                            }}
                            label="Acquisitie naam"
                            variant="bordered"
                            color={editSubjectError ? "danger" : "default"}
                            description={`${editedSubject.length}/40 tekens`}
                            errorMessage={editSubjectError}
                            className="mb-4"
                        />
                        <div className="flex justify-end gap-2 mt-4 mb-2">
                            <Button 
                                onClick={handleCancelEdit}
                                variant="light"
                            >
                                Annuleren
                            </Button>
                            <Button 
                                onClick={handleEditSubject}
                                color="primary"
                                isDisabled={!!editSubjectError}
                            >
                                Opslaan
                            </Button>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}; 